import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, GitCompare, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AdvancedCompare from "./AdvancedCompare";

const LIST_KEY = "dashboard_compare_widgets_list";
const ITEM_PREFIX = "dashboard_compare_widget_";

const newWidget = (idx) => ({
  id: `w_${Date.now()}_${idx}`,
  name: `Comparaison ${idx + 1}`,
});

/**
 * Conteneur multi-widgets de comparaison sur le tableau de bord.
 * - Liste des widgets persistée via /user-table-prefs (LIST_KEY → sort.widgets)
 * - Config de chaque widget persistée séparément (ITEM_PREFIX + id) par AdvancedCompare
 */
export default function DashboardCompareWidgets({ orderTypes = [], sellersList = [] }) {
  const [widgets, setWidgets] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Load list of widgets
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.getUserTablePref(LIST_KEY);
        const list = data?.sort?.widgets;
        if (Array.isArray(list) && list.length > 0) {
          setWidgets(list);
        } else {
          setWidgets([newWidget(0)]);
        }
      } catch (_e) {
        setWidgets([newWidget(0)]);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const persistList = useCallback(async (next) => {
    try {
      await api.upsertUserTablePref(LIST_KEY, { sort: { widgets: next } });
    } catch (_e) { /* silent */ }
  }, []);

  const handleAdd = () => {
    setWidgets((prev) => {
      const next = [...prev, newWidget(prev.length)];
      persistList(next);
      return next;
    });
  };

  const startRename = (w) => {
    setEditingId(w.id);
    setEditName(w.name);
  };

  const commitRename = () => {
    const name = editName.trim();
    if (!name) { setEditingId(null); return; }
    setWidgets((prev) => {
      const next = prev.map((w) => (w.id === editingId ? { ...w, name } : w));
      persistList(next);
      return next;
    });
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    setWidgets((prev) => {
      const next = prev.filter((w) => w.id !== id);
      persistList(next);
      return next;
    });
    // Also delete this widget's config
    try { await api.deleteUserTablePref(`${ITEM_PREFIX}${id}`); } catch (_e) { /* silent */ }
    setConfirmDelete(null);
    toast.success("Vue supprimée");
  };

  if (!loaded) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Chargement…
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="dashboard-compare-widgets">
      <Card className="bg-zinc-50 border-dashed">
        <CardHeader className="py-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <GitCompare className="h-4 w-4 text-primary" />
              Mes vues de comparaison
              <span className="text-xs font-normal text-muted-foreground">
                ({widgets.length} vue{widgets.length > 1 ? "s" : ""})
              </span>
            </CardTitle>
            <Button size="sm" variant="default" onClick={handleAdd} data-testid="dashboard-compare-add-widget">
              <Plus className="h-3.5 w-3.5 mr-1" /> Nouvelle vue
            </Button>
          </div>
        </CardHeader>
      </Card>

      {widgets.map((w) => (
        <div key={w.id} className="space-y-1" data-testid={`compare-widget-${w.id}`}>
          <div className="flex items-center justify-between gap-2 px-1">
            {editingId === w.id ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitRename();
                    if (e.key === "Escape") setEditingId(null);
                  }}
                  className="h-8 max-w-sm"
                  autoFocus
                  data-testid={`compare-widget-name-input-${w.id}`}
                />
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={commitRename}>
                  <Check className="h-3.5 w-3.5 text-emerald-600" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingId(null)}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <>
                <h3 className="font-heading text-sm sm:text-base flex items-center gap-2">
                  <GitCompare className="h-4 w-4 text-primary" />
                  {w.name}
                </h3>
                <div className="flex items-center gap-1">
                  <Button
                    size="icon" variant="ghost" className="h-7 w-7"
                    onClick={() => startRename(w)}
                    title="Renommer"
                    data-testid={`compare-widget-rename-${w.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {widgets.length > 1 && (
                    <Button
                      size="icon" variant="ghost" className="h-7 w-7"
                      onClick={() => setConfirmDelete(w)}
                      title="Supprimer"
                      data-testid={`compare-widget-delete-${w.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
          <AdvancedCompare
            orderTypes={orderTypes}
            sellersList={sellersList}
            persistKey={`${ITEM_PREFIX}${w.id}`}
            height={340}
            compact
          />
        </div>
      ))}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette vue ?</AlertDialogTitle>
            <AlertDialogDescription>
              La configuration de « {confirmDelete?.name} » sera définitivement supprimée. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => confirmDelete && handleDelete(confirmDelete.id)}
              data-testid="confirm-delete-widget"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
